import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import {
  decommissionStore,
  getProvisionedStore,
  getRegistersByStore,
} from '../../../../utils/service-calls/fiskaly';
import { ButtonBlack, ButtonRed } from '../../../reusable';

const compareDatesDescending = (a, b) => {
  const timeA = a ? new Date(a).getTime() : 0;
  const timeB = b ? new Date(b).getTime() : 0;
  return timeB - timeA;
};

const sortByStatusAndDate = (a, b) => {
  const aActive = a.status === 'ACTIVE';
  const bActive = b.status === 'ACTIVE';
  if (aActive !== bActive) return aActive ? -1 : 1;
  if (aActive) return compareDatesDescending(a.provisionedAt, b.provisionedAt);
  return compareDatesDescending(a.decommissionedAt, b.decommissionedAt);
};

const InfoRow = ({ label, value }) => (
  <article className="ncss-col-sm-6 va-sm-t mb2-sm">
    <p className="body-4" style={{ color: '#757575', margin: 0 }}>{label}</p>
    <p className="body-2" style={{ margin: 0, wordBreak: 'break-all' }}>
      {value === undefined || value === null || value === '' ? '—' : value}
    </p>
  </article>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

InfoRow.defaultProps = {
  value: '',
};

const StoreInfo = ({
  userToken, store: initialStore, onBack, onDecommissioned, onProvisionRegister, onViewRegister,
}) => {
  const { storeId } = initialStore;
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [store, setStore] = useState(initialStore);
  const [registers, setRegisters] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [decommissioningStore, setDecommissioningStore] = useState(false);

  const fetchStoreAndRegisters = useCallback(() => {
    setFetchError('');
    setIsLoading(true);
    return Promise.all([
      getProvisionedStore(userToken, storeId),
      getRegistersByStore(userToken, storeId),
    ])
      .then(([storeRes, registersRes]) => {
        setStore(storeRes.body);
        const sortedRegisters = [...(registersRes.body?.registers || [])].sort(sortByStatusAndDate);
        setRegisters(sortedRegisters);
        return null;
      })
      .catch((err) => setFetchError(err.message || 'Failed to load store details'))
      .finally(() => setIsLoading(false));
  }, [userToken, storeId]);

  useEffect(() => {
    fetchStoreAndRegisters();
  }, [fetchStoreAndRegisters]);

  const handleDecommissionStore = () => {
    setDecommissioningStore(true);
    setErrorMessage('');
    return decommissionStore(userToken, storeId)
      .then(() => {
        onDecommissioned(storeId);
        return null;
      })
      .catch((err) => {
        setErrorMessage(err.message || `Failed to decommission store ${storeId}`);
        setDecommissioningStore(false);
      });
  };

  const decommissionedRegistersCount = registers
    .filter((r) => r.status === 'DECOMMISSIONED').length;
  const hasNonDecommissionedRegisters = registers.length !== decommissionedRegistersCount;
  const isDecommissioned = store.status === 'DECOMMISSIONED';

  const renderRegistersTable = () => (
    <table className="soa-table">
      <thead>
        <tr>
          <th>Register ID</th>
          <th style={{ width: 140 }}>Status</th>
          <th aria-label="View" style={{ width: 90 }} />
        </tr>
      </thead>
      <tbody>
        {registers.length === 0 ? (
          <tr>
            <td className="soa-empty" colSpan={3}>No registers provisioned</td>
          </tr>
        ) : (
          registers.map((register, idx) => (
            <tr key={register.registerId} className={idx % 2 === 0 ? 'soa-row-even' : ''}>
              <td>{register.registerId}</td>
              <td>{register.status}</td>
              <td style={{ textAlign: 'center' }}>
                <button
                  className="soa-row-edit-toggle"
                  title="View"
                  type="button"
                  onClick={() => onViewRegister(register)}
                >
                  View
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  return (
    <main className="ncss-col-sm-12 ta-sm-l">
      <div
        className="mb2-sm"
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: '12px',
          justifyContent: 'space-between',
        }}
      >
        {!isLoading && <ButtonBlack label="Back to Stores" onClick={onBack} />}
        {!isLoading && !fetchError && !isDecommissioned && (
          <div style={{ alignItems: 'flex-end', display: 'flex', flexDirection: 'column' }}>
            <ButtonRed
              confirmationMessage={`Do you really want to decommission store ${storeId}?`}
              isDisabled={hasNonDecommissionedRegisters || decommissioningStore}
              isLoading={decommissioningStore}
              label="Decommission Store"
              onClick={handleDecommissionStore}
            />
            {hasNonDecommissionedRegisters && (
              <p className="body-4 mt1-sm" style={{ color: '#b64d4d', margin: '4px 0 0' }}>
                Decommission all registers below before decommissioning this store.
              </p>
            )}
          </div>
        )}
      </div>
      {errorMessage && (
        <p className="text-color-error ta-sm-c body-2 mt2-sm">
          {errorMessage}
        </p>
      )}
      {(() => {
        if (isLoading) return <Loading />;
        if (fetchError) return <section className="text-color-error body-2 mt2-sm">{fetchError}</section>;
        return (
          <>
            <section className="bg-primary-grey pt2-sm pb2-sm prl2-sm border mb4-sm">
              <header className="u-rounded bg-black va-sm-m pt2-sm pb2-sm mb2-sm">
                <span className="headline-3 pl3-sm text-color-primary-light">
                  Store Info
                </span>
              </header>
              <div className="ncss-row">
                <InfoRow label="Store ID" value={store.storeId} />
                <InfoRow label="Store Name" value={store.storeName} />
                <InfoRow label="Siret" value={store.siret} />
                <InfoRow label="Status" value={store.status} />
                <InfoRow label="Fiskaly Location ID" value={store.fiskalyLocationId} />
                <InfoRow label="Street" value={store.address?.street} />
                <InfoRow label="Street Number" value={store.address?.streetNumber} />
                <InfoRow label="Postal Code" value={store.address?.postalCode} />
                <InfoRow label="City" value={store.address?.city} />
                <InfoRow label="Provisioned By" value={store.provisionedBy} />
                <InfoRow label="Provisioned At" value={store.provisionedAt} />
                <InfoRow label="Decommissioned At" value={store.decommissionedAt} />
              </div>
            </section>

            <section className="bg-primary-grey pt2-sm pb2-sm prl2-sm border">
              <header className="u-rounded bg-black va-sm-m pt2-sm pb2-sm mb2-sm">
                <span className="headline-3 pl3-sm text-color-primary-light">
                  Registers
                </span>
              </header>
              {renderRegistersTable()}
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <ButtonBlack className="mt2-sm" label="Provision Register" onClick={onProvisionRegister} />
              </div>
            </section>
          </>
        );
      })()}
    </main>
  );
};

StoreInfo.defaultProps = {
  onDecommissioned: () => null,
};

StoreInfo.propTypes = {
  onBack: PropTypes.func.isRequired,
  onDecommissioned: PropTypes.func,
  onProvisionRegister: PropTypes.func.isRequired,
  onViewRegister: PropTypes.func.isRequired,
  store: PropTypes.shape({
    address: PropTypes.shape({
      city: PropTypes.string,
      postalCode: PropTypes.string,
      street: PropTypes.string,
      streetNumber: PropTypes.string,
    }),
    decommissionedAt: PropTypes.string,
    fiskalyLocationId: PropTypes.string,
    provisionedAt: PropTypes.string,
    provisionedBy: PropTypes.string,
    siret: PropTypes.string,
    status: PropTypes.string,
    storeId: PropTypes.string.isRequired,
    storeName: PropTypes.string,
  }).isRequired,
  userToken: PropTypes.string.isRequired,
};

export default StoreInfo;
